import { useEffect, useState } from "react";
import { usePublicBooking, type BookingEvent } from "./use-public-booking";
import { usePublicCustomer } from "./use-public-customer";

export function useCustomerFlow(publicId: string | undefined) {
  const { data: customer } = usePublicCustomer(publicId || "");
  const { data: events } = usePublicBooking(publicId || "");

  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BookingEvent | null>(null);
  const [hasShownCustomerDialog, setHasShownCustomerDialog] = useState(false);

  useEffect(() => {
    if (!customer || !events) return;

    // Only consolidate a customer we actually know something about: an existing
    // customer record with at least one contact (phone or email). Nagging an
    // anonymous web visitor (no known contact) to "confirm" empty fields makes
    // no sense, so skip the dialog entirely in that case.
    const knownContact = !!customer.phone || !!customer.email;
    const canConsolidate = !!customer.id && knownContact;
    // Identified = a proven contact channel (verified phone or email). A phone
    // is proven implicitly on a call and explicitly via web OTP.
    const isIdentified =
      !!customer.phone_is_verified || !!customer.email_is_verified;
    // What this dialog is actually for: filling the gaps in a half-known contact.
    // It used to stop nagging because submitting it marked the email as verified —
    // self-certification we no longer do, so ask the real question instead. Once the
    // details are complete the prompt stays away, verified or not.
    const detailsIncomplete =
      !customer.phone ||
      !customer.email ||
      !customer.given_name ||
      !customer.family_name;

    // Priority 1: we only know part of how to reach them → prompt once to complete it.
    if (canConsolidate && detailsIncomplete && !hasShownCustomerDialog) {
      setShowCustomerDialog(true);
      setShowEventDialog(false);
      setHasShownCustomerDialog(true);
      return;
    }

    // Priority 2: Check for future unconfirmed events (only once identified)
    if (isIdentified) {
      const now = Date.now();
      const futureUnconfirmedEvents = events.filter(
        (event) => event.start_time > now && event.status !== "confirmed",
      );

      if (
        futureUnconfirmedEvents.length > 0 &&
        !showEventDialog &&
        !showCustomerDialog
      ) {
        // Show the first unconfirmed event
        setSelectedEvent(futureUnconfirmedEvents[0]);
        setShowEventDialog(true);
      }
    }
  }, [
    customer,
    events,
    hasShownCustomerDialog,
    showCustomerDialog,
    showEventDialog,
  ]);

  const handleCustomerDialogClose = (open: boolean) => {
    setShowCustomerDialog(open);

    // When customer dialog closes after successful update,
    // the useEffect will automatically check and show event dialog if needed
  };

  const handleEventDialogClose = (open: boolean) => {
    setShowEventDialog(open);
    if (!open) {
      setSelectedEvent(null);

      // Check if there are more unconfirmed events to show
      const identified =
        !!customer?.phone_is_verified || !!customer?.email_is_verified;
      if (identified && events) {
        const now = Date.now();
        const futureUnconfirmedEvents = events.filter(
          (event) =>
            event.start_time > now &&
            event.status !== "confirmed" &&
            event.id !== selectedEvent?.id, // Don't show the same event again
        );

        if (futureUnconfirmedEvents.length > 0) {
          setSelectedEvent(futureUnconfirmedEvents[0]);
          setShowEventDialog(true);
        }
      }
    }
  };

  return {
    customer,
    events,
    showCustomerDialog,
    showEventDialog,
    selectedEvent,
    handleCustomerDialogClose,
    handleEventDialogClose,
  };
}
